import { subDays, subMonths, startOfYear, startOfDay, endOfDay, isAfter } from 'date-fns';

import type { DateRangePayload, DateRangeState } from './storeTypes';

// This is exported so other components can know the default date range without hard coding 3 months
// That way we can keep the date range consistent across the app if it ever changes
export const DEFAULT_DATE_RANGE: DateRangeState = {
	dateRange: '3M',
	fromDate: subMonths(startOfDay(new Date()), 3),
	toDate: new Date(),
};

type DateRangeParams = {
	payload?: DateRangePayload;
	entityCreationDate?: string;
};
// Builds the date related state properties for any given range
// It will default to 3M if no range has been provided
export const buildDateRangeState = (dateRangeParams?: DateRangeParams): DateRangeState => {
	const startOfToday = startOfDay(new Date());
	const now = new Date();
	const payload = dateRangeParams?.payload;
	switch (payload?.range) {
		case '7D':
			return {
				dateRange: payload.range,
				fromDate: subDays(startOfToday, 7),
				toDate: now,
			};
		case '1M':
			return {
				dateRange: payload.range,
				fromDate: subMonths(startOfToday, 1),
				toDate: now,
			};
		case '3M':
			return {
				dateRange: payload?.range || '3M',
				fromDate: subMonths(startOfToday, 3),
				toDate: now,
			};
		case 'YTD':
			return {
				dateRange: payload.range,
				fromDate: startOfYear(startOfToday),
				toDate: now,
			};
		case 'AllTime':
			return {
				dateRange: payload.range,
				fromDate: startOfDay(payload.fromDate),
				toDate: now,
			};
		case 'Custom':
			return {
				dateRange: payload.range,
				fromDate: payload.fromDate, // calendar picker will always provides the date at the start of the day
				// calendar picker will always provides the date at the start of the day, but "toDate" should be the end of the day or if it is today, it should be the current time
				toDate: isAfter(endOfDay(payload.toDate), now) ? now : endOfDay(payload.toDate),
			};
		// if a starting date is provided, check its within the default range
		// if its not, return it as the start, else use the default range.
		default: {
			const entityCreationDate = dateRangeParams?.entityCreationDate;
			const startingDate = entityCreationDate ? new Date(entityCreationDate) : undefined;
			if (startingDate && startingDate > DEFAULT_DATE_RANGE.fromDate) {
				return {
					dateRange: 'Custom',
					fromDate: startOfDay(startingDate),
					toDate: now,
				};
			}
			return DEFAULT_DATE_RANGE;
		}
	}
};
